import type { ProductDetailFeatureOptions } from '../../../api/models/FeatureOptions';
import type { ProductDetailFeatureItem } from '../../../models/features/main/ProductDetailFeatureItem';
import type { AppDispatch, RootState } from '../../../store/store';
import { apiClient } from '../../ApiClient';
import { getProductList } from '../../../api/functions/getProductList';
import {
    getNewFeatureUuid,
    getDefaultBackableFeatureItem,
    defaultBackToPreviousFeatureItems
} from '../../FeatureService';
import type { FeatureHandler } from '../FeatureHandler';
import { dispatchEvent } from '../../EventService';

export const productDetailMainFeatureHandler = {
    async startFeatureItem(
        dispatch: AppDispatch,
        state: RootState,
        featureOptions: ProductDetailFeatureOptions
    ): Promise<void> {
        const newMainFeatureItem: ProductDetailFeatureItem = {
            featureUuid: getNewFeatureUuid('product-detail'),
            featureType: 'product-detail',
            productId: featureOptions.productId,
            productLoadingStatus: 'loading',
            similarLoadingStatus: 'loading',
            combinationsLoadingStatus: 'loading',
            ...getDefaultBackableFeatureItem(state)
        };
        dispatch.featureState.setMenuFeatureItem(null);
        dispatch.featureState.setMainFeatureItem(newMainFeatureItem);
        dispatch.uiState.setMode('full');
        await dispatch.featureState.startFeature({
            featureType: 'chat',
            chatType: 'product-detail'
        });
        try {
            const product =
                featureOptions.product ??
                (await apiClient.product.getProductByProductId({
                    params: { productId: featureOptions.productId }
                }));
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                product,
                productLoadingStatus: 'success'
            });
            const [combinations, similar] = await Promise.all([
                getProductList({
                    queryType: 'combinations',
                    combineWithProductIds: [featureOptions.productId]
                }),
                getProductList({
                    queryType: 'similar',
                    baseProductsIds: [featureOptions.productId]
                })
            ]);
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                product,
                productLoadingStatus: 'success',
                combinations,
                combinationsLoadingStatus: 'success',
                similar,
                similarLoadingStatus: 'success'
            });
            await dispatch.featureState.updateContext();
            if (featureOptions.initialQuestion) {
                dispatch.featureState.appendNewChatMessage({
                    role: 'user',
                    content: {
                        contentType: 'plain-text',
                        text: featureOptions.initialQuestion
                    }
                });
            }
            dispatch.featureState.setSidebarExpanded(true);
            dispatchEvent({
                type: 'bri.view_product_detail',
                isInteraction: true,
                ...(featureOptions.source === 'brilliant'
                    ? { brilliantSource: 'ui', source: 'brilliant' }
                    : { source: 'web' }),
                productId: featureOptions.productId
            });
        } catch {
            dispatch.featureState.updateMainFeatureItem({
                ...newMainFeatureItem,
                productLoadingStatus: 'error',
                combinationsLoadingStatus: 'error',
                similarLoadingStatus: 'error'
            });
            await dispatch.featureState.updateContext();
        }
    },
    isBackable: true,
    backToPreviousFeatureItems(dispatch: AppDispatch, state: RootState): void {
        defaultBackToPreviousFeatureItems(dispatch, state);
    }
} as const satisfies FeatureHandler<ProductDetailFeatureItem>;
